import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Mail, ChevronDown } from "lucide-react";
import { Container, Section, Reveal, Eyebrow, SectionHeading } from "@/components/common";
import { Icon, PageHero, CTABand } from "@/components/ui-extras";
import { PRODUCTS, PRODUCT_CATEGORIES, PRIVATE_LABEL_CONTENT, COMPANY, IMG, img } from "@/data/content";

const ProductModal = ({ product, onClose }) => {
  if (!product) return null;
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        style={{ background: "rgba(2,6,6,0.74)", backdropFilter: "blur(4px)" }} data-testid="product-modal"
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full sm:max-w-4xl bg-white sm:rounded-[var(--radius)] overflow-hidden my-0 sm:my-8"
          style={{ border: "1px solid var(--pt-border-gold)", boxShadow: "var(--shadow-card)" }}
        >
          <button onClick={onClose} data-testid="product-modal-close" aria-label="Close" className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ background: "rgba(2,6,6,0.55)" }}>
            <X size={20} />
          </button>
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative h-56 sm:h-72 lg:h-full min-h-[220px]">
              <img src={img(product.image, 900)} alt={product.name} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(2,6,6,0) 50%, rgba(2,6,6,0.4) 100%)" }} />
            </div>
            <div className="p-6 sm:p-8">
              <h2 className="font-serif text-3xl leading-tight" style={{ color: "var(--pt-navy)" }}>{product.name}</h2>
              <p className="mt-4 font-sans text-sm leading-relaxed" style={{ color: "var(--pt-muted)" }}>{product.bio}</p>
            </div>
          </div>
          <div className="px-6 sm:px-8 pb-8">
            <h3 className="font-serif text-xl mb-3" style={{ color: "var(--pt-navy)" }}>Key Parameters</h3>
            <div className="overflow-hidden rounded-[var(--radius)]" style={{ border: "1px solid var(--pt-border)" }}>
              <table className="w-full border-collapse" data-testid="product-param-table">
                <tbody>
                  {product.params.map(([k, v], i) => (
                    <tr key={k} style={{ background: i % 2 === 0 ? "var(--pt-paper)" : "#fff" }}>
                      <td className="align-top px-4 py-2.5 font-sans text-[13px] font-semibold w-1/3" style={{ color: "var(--pt-navy)", borderBottom: "1px solid var(--pt-border)" }}>{k}</td>
                      <td className="align-top px-4 py-2.5 font-sans text-[13px]" style={{ color: "var(--pt-muted)", borderBottom: "1px solid var(--pt-border)" }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <a href={`mailto:${COMPANY.email}`} data-testid="product-modal-email" className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-sans text-sm font-semibold" style={{ background: "linear-gradient(90deg,#3ABBB0,#41E19A)", color: "#020606" }}>
              <Mail size={16} /> Email Us About {product.name.split(" ").slice(0, 1).join(" ")}
            </a>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const PrivateLabelBlock = () => (
  <div data-testid="private-label-block" className="mt-10">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <Reveal className="lg:col-span-7">
        <SectionHeading eyebrow="Private Label Products" title="Your Brand, Export-Ready & Built to Scale" />
        <div className="mt-5 space-y-4">
          {PRIVATE_LABEL_CONTENT.bio.map((p, i) => (
            <p key={i} className="font-sans text-base leading-relaxed" style={{ color: "var(--pt-muted)" }}>{p}</p>
          ))}
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-[var(--radius)] p-4" style={{ background: "linear-gradient(150deg,#E8FFF6,#DDFCF5)", border: "1px solid #B8EFE3" }}>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ background: "linear-gradient(140deg,#3ABBB0,#41E19A)" }}>
            <Icon name="BadgeCheck" size={18} color="#FFFFFF" />
          </span>
          <p className="font-sans text-sm leading-relaxed" style={{ color: "#063B36" }}>
            Private label support is backed by Prince Trading Impex's <span className="font-semibold">ISO 22000:2018</span> certification, strengthening buyer confidence in food safety management, packaging coordination, and export-ready product handling.
          </p>
        </div>
      </Reveal>
      <Reveal className="lg:col-span-5" delay={0.1}>
        <div className="relative overflow-hidden rounded-[var(--radius)]" style={{ boxShadow: "var(--shadow-card)" }}>
          <img src={img(IMG.plainPackaging, 800)} alt="Plain export-ready private label packaging" className="aspect-[4/3] w-full object-cover" />
        </div>
      </Reveal>
    </div>

    <h3 className="mt-14 mb-6 font-serif text-2xl" style={{ color: "var(--pt-navy)" }}>Private Label Capabilities</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {PRIVATE_LABEL_CONTENT.capabilities.map((c, i) => (
        <Reveal key={c.title} delay={(i % 4) * 0.04}>
          <div className="h-full card-hover rounded-[var(--radius)] bg-white p-5" style={{ border: "1px solid var(--pt-border-gold)", boxShadow: "var(--shadow-soft)" }}>
            <div className="flex h-11 w-11 items-center justify-center rounded-full" style={{ background: "rgba(58,187,176,0.10)" }}>
              <Icon name={c.icon} size={18} color="#0E8C82" />
            </div>
            <h4 className="mt-4 font-serif text-lg leading-tight" style={{ color: "var(--pt-navy)" }}>{c.title}</h4>
            <p className="mt-2 font-sans text-[13px] leading-relaxed" style={{ color: "var(--pt-muted)" }}>{c.text}</p>
          </div>
        </Reveal>
      ))}
    </div>

    <h3 className="mt-14 mb-4 font-serif text-2xl" style={{ color: "var(--pt-navy)" }}>Private Label Parameters</h3>
    <div className="overflow-hidden rounded-[var(--radius)]" style={{ border: "1px solid var(--pt-border)" }}>
      <table className="w-full border-collapse">
        <tbody>
          {PRIVATE_LABEL_CONTENT.params.map(([k, v], i) => (
            <tr key={k} style={{ background: i % 2 === 0 ? "var(--pt-paper)" : "#fff" }}>
              <td className="align-top px-4 py-2.5 font-sans text-[13px] font-semibold w-1/3" style={{ color: "var(--pt-navy)", borderBottom: "1px solid var(--pt-border)" }}>{k}</td>
              <td className="align-top px-4 py-2.5 font-sans text-[13px]" style={{ color: "var(--pt-muted)", borderBottom: "1px solid var(--pt-border)" }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <a href={`mailto:${COMPANY.email}`} className="mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-sans text-sm font-semibold" style={{ background: "linear-gradient(90deg,#3ABBB0,#41E19A)", color: "#020606" }}>
      <Mail size={16} /> Email Us About Private Label
    </a>
  </div>
);

const Products = () => {
  const [activeCat, setActiveCat] = useState(null);
  const [selected, setSelected] = useState(null);

  const catProducts = useMemo(
    () => (activeCat && activeCat !== "private-label" ? PRODUCTS.filter((p) => p.category === activeCat) : []),
    [activeCat]
  );
  const activeCatObj = PRODUCT_CATEGORIES.find((c) => c.id === activeCat);

  return (
    <div>
      <PageHero
        eyebrow="Our Products"
        title="Premium Agricultural Commodity Catalogue"
        intro="Explore our five core export categories. Select a category to view individual products, full parameters, packaging, and customization options."
        image={img(IMG.basmati, 1600)}
      />

      {/* Category tiles */}
      <Section className="bg-white">
        <Container>
          <SectionHeading center eyebrow="Product Categories" title="Choose a Category" className="mb-6" />
          <div className="mb-12 flex flex-col items-center text-center">
            <span
              data-testid="products-iso-badge"
              className="inline-flex items-center gap-2.5 rounded-full py-2 pl-2.5 pr-4 font-sans text-xs sm:text-[13px] font-semibold"
              style={{ background: "linear-gradient(150deg,#E8FFF6,#DDFCF5)", border: "1px solid #B8EFE3", color: "#063B36" }}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ background: "linear-gradient(140deg,#3ABBB0,#41E19A)" }}>
                <Icon name="BadgeCheck" size={15} color="#FFFFFF" />
              </span>
              ISO 22000:2018 Certified Export House
            </span>
            <p className="mt-4 max-w-2xl font-sans text-sm leading-relaxed" style={{ color: "var(--pt-muted)" }}>
              Our product portfolio is supported by structured sourcing, inspection, packaging supervision, and food safety management practices aligned with ISO 22000:2018 certification.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-category-grid">
            {PRODUCT_CATEGORIES.map((c, i) => {
              const on = activeCat === c.id;
              return (
                <Reveal key={c.id} delay={(i % 3) * 0.05}>
                  <button
                    data-testid={`product-category-${c.id}`}
                    onClick={() => { setActiveCat(on ? null : c.id); if (!on) setTimeout(() => document.getElementById("category-detail")?.scrollIntoView({ behavior: "smooth", block: "center" }), 120); }}
                    className="group relative flex h-full w-full flex-col text-left card-hover rounded-[var(--radius)] overflow-hidden"
                    style={{ border: on ? "2px solid #3ABBB0" : "1px solid var(--pt-border-gold)", boxShadow: on ? "var(--shadow-gold-glow)" : "var(--shadow-soft)" }}
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={img(c.image, 700)} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(2,6,6,0) 35%, rgba(2,6,6,0.55) 100%)" }} />
                      <h3 className="absolute bottom-4 left-5 font-serif text-2xl text-white">{c.name}</h3>
                    </div>
                    <div className="flex flex-1 flex-col bg-white p-5">
                      <p className="font-sans text-sm leading-relaxed" style={{ color: "var(--pt-muted)" }}>{c.blurb}</p>
                      <span className="mt-auto pt-4 inline-flex items-center gap-1.5 font-sans text-sm font-semibold" style={{ color: "var(--pt-gold)" }}>
                        {on ? "Hide" : "View Products"} <ChevronDown size={15} className={`transition-transform ${on ? "rotate-180" : ""}`} />
                      </span>
                    </div>
                  </button>
                </Reveal>
              );
            })}
          </div>

          {/* Detail panel */}
          <div id="category-detail">
            <AnimatePresence mode="wait">
              {activeCat && (
                <motion.div
                  key={activeCat}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.35 }}
                  className="mt-14 rounded-[var(--radius)] p-6 sm:p-8" style={{ background: "var(--pt-paper)", border: "1px solid var(--pt-border-gold)" }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {activeCat !== "private-label" && <Eyebrow>{activeCatObj?.name}</Eyebrow>}
                  </div>
                  {activeCat === "private-label" ? (
                    <PrivateLabelBlock />
                  ) : (
                    <>
                      <h3 className="font-serif text-3xl mb-6" style={{ color: "var(--pt-navy)" }}>{activeCatObj?.name} Products</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {catProducts.map((p, i) => (
                          <Reveal key={p.slug} delay={(i % 3) * 0.04}>
                            <button
                              data-testid="product-card"
                              onClick={() => setSelected(p)}
                              className="group flex h-full w-full flex-col text-left card-hover rounded-[var(--radius)] bg-white overflow-hidden"
                              style={{ border: "1px solid var(--pt-border-gold)", boxShadow: "var(--shadow-soft)" }}
                            >
                              <div className="relative aspect-[16/11] overflow-hidden">
                                <img src={img(p.image, 600)} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                              </div>
                              <div className="flex flex-1 flex-col p-5">
                                <h4 className="font-serif text-xl leading-tight" style={{ color: "var(--pt-navy)" }}>{p.name}</h4>
                                <p className="mt-2 font-sans text-sm leading-relaxed line-clamp-2" style={{ color: "var(--pt-muted)" }}>{p.bio}</p>
                                <span className="mt-auto pt-4 inline-flex items-center gap-1.5 font-sans text-sm font-semibold" style={{ color: "var(--pt-gold)" }}>
                                  View Details <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                                </span>
                              </div>
                            </button>
                          </Reveal>
                        ))}
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Container>
      </Section>

      {selected && <ProductModal product={selected} onClose={() => setSelected(null)} />}

      <CTABand
        heading="Request product specifications & pricing."
        subtext="Share your commodity, grade, quantity, and destination — email us and we'll respond with tailored details."
      />
    </div>
  );
};

export default Products;
